import { orderBy } from 'lodash'

import { CreateProjectDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { OneCreationActionResolver } from 'modules/core/lib/oneCreateActions/types'
import { useNavigation } from 'modules/core/hooks/useNavigation'

export const useCreateProject: OneCreationActionResolver = () => {
  const { company, employee } = useCurrentSession()
  const { navigation } = useNavigation()

  const [createProject, { loading }] = useMutation(CreateProjectDocument, {
    extractorFn: (data) => data.projectManagementMutations.createProjectmanagementProject,
    errorPolicy: 'all',
  })

  const createProjectHandler = async (args: {
    name: string
    code?: string
    startDate?: string
    dueDate?: string
    isBillable?: boolean
    description?: string
  }) => {
    if (!employee) {
      return
    }

    const mainLegalEntityId = orderBy(company.legalEntities, 'main', 'desc')[0]?.id
    if (!mainLegalEntityId) {
      return
    }

    const { name, code, startDate, dueDate, isBillable = false, description } = args

    const result = await createProject({
      variables: {
        name: name.trim(),
        code: code?.trim() ?? undefined,
        description: description?.trim(),
        startDate: startDate,
        dueDate: dueDate,
        isBillable: isBillable,
        legalEntityId: mainLegalEntityId,
        projectAdmins: [employee.id],
        projectManagers: [],
        projectViewers: [],
        status: 'active',
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success && handled.data.project?.id) {
      return {
        to: navigation.projects.id.details.settings.fullPath({
          id: String(handled.data.project.id),
        }),
        data: args,
      }
    }

    return handled
  }

  return {
    resolver: createProjectHandler,
    loading,
  }
}
